"use client"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

interface CTAProps {
  badge?: {
    text: string
  }
  title: string
  description?: string
  action: {
    text: string
    href: string
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost"
  }
  withGlow?: boolean
  className?: string
}

export function CTASection({
  badge,
  title,
  description,
  action,
  withGlow = true,
  className,
}: CTAProps) {
  return (
    <div className="mx-6 my-8 md:mx-8">
      <section className={cn("relative py-24 md:py-32 rounded-[48px] overflow-hidden", className)}>
        {/* Content */}
        <div className="relative mx-auto flex max-w-container flex-col items-center gap-8 px-6 text-center sm:gap-10 md:px-8">
          {/* Badge */}
          {badge && (
            <Badge
              variant="outline"
              className="opacity-0 animate-fade-in-up delay-100 border-primary/20 bg-background/50 px-4 py-1.5 text-base backdrop-blur-sm"
            >
              <span className="text-primary font-medium">
                {badge.text}
              </span>
            </Badge>
          )}

          {/* Title */}
          <h2 className={cn(
            "opacity-0 animate-fade-in-up delay-200",
            "text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl lg:text-5xl",
            "leading-[1.15] sm:leading-[1.15]",
            "bg-gradient-to-b from-foreground via-foreground/90 to-muted-foreground",
            "bg-clip-text text-transparent"
          )}>
            {title}
          </h2>

          {/* Description */}
          {description && (
            <p className="max-w-2xl text-base sm:text-lg md:text-xl text-muted-foreground opacity-0 animate-fade-in-up delay-300 leading-relaxed">
              {description}
            </p>
          )}

          {/* Action Button */}
          <Button
            variant={action.variant || "default"}
            size="lg"
            className="opacity-0 animate-fade-in-up delay-500 px-8 py-6 text-lg font-medium rounded-full transition-all duration-300 bg-primary hover:bg-primary/90 mb-12"
            asChild
          >
            <a href={action.href}>{action.text}</a>
          </Button>

          {/* Bottom Gradient Effect */}
          <div 
            className="absolute inset-0 -z-10 h-full w-full bg-gradient-to-t from-primary/20 via-primary/5 to-background rounded-[48px]" 
            style={{ 
              maskImage: 'linear-gradient(to bottom, transparent, black)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent, black)'
            }} 
          />
        </div>
      </section>
    </div>
  )
} 